import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const STUN = { iceServers: [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }] };
const POLL_MS = 1000;
const CONNECT_TIMEOUT_MS = 20000;
const newId = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9-]/g, "");

// Sinalização WebRTC por HTTP + MongoDB: sobrevive a proxies, múltiplas réplicas e quedas de rede.
export const useRtcSession = ({ roomId, displayName, role = "", onRemoteStream }) => {
  const [status, setStatus] = useState("init");
  const [remoteStream, setRemoteStream] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const s = useRef({ peerId: newId(), pc: null, target: null, cursor: "", pending: [], attempts: 0, startedAt: 0, ice: STUN, alive: true });

  useEffect(() => {
    const st = s.current;
    st.alive = true;
    const base = `${API}/api/rtc2/${encodeURIComponent(roomId)}`;
    const signal = (to, type, data) => fetch(`${base}/signal`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ peer_id: st.peerId, to, type, data }),
    }).catch(() => {});

    const closePc = () => {
      if (st.pc) { st.pc.onicecandidate = null; st.pc.ontrack = null; st.pc.onconnectionstatechange = null; try { st.pc.close(); } catch { /* noop */ } }
      st.pc = null; st.pending = []; setRemoteStream(null);
    };

    const createPc = (target) => {
      closePc();
      const relay = st.attempts > 0 && st.attempts % 2 === 1;
      const pc = new RTCPeerConnection({ ...st.ice, ...(relay ? { iceTransportPolicy: "relay" } : {}) });
      pc._target = target;
      st.localStream?.getTracks().forEach((t) => pc.addTrack(t, st.localStream));
      pc.onicecandidate = (e) => { if (e.candidate) signal(target, "ice", e.candidate.toJSON()); };
      pc.ontrack = (e) => {
        const rs = e.streams[0] || new MediaStream([e.track]);
        setRemoteStream(rs); onRemoteStream?.(rs);
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === "connected") { st.attempts = 0; setStatus("connected"); }
        if (pc.connectionState === "failed") { st.attempts += 1; closePc(); setStatus("connecting"); }
      };
      st.pc = pc; st.startedAt = Date.now();
      return pc;
    };

    const flushIce = async () => {
      const list = st.pending; st.pending = [];
      for (const c of list) { try { await st.pc?.addIceCandidate(c); } catch { /* noop */ } }
    };

    const offer = async (target) => {
      const pc = createPc(target);
      setStatus("connecting");
      await pc.setLocalDescription(await pc.createOffer());
      signal(target, "offer", { type: pc.localDescription.type, sdp: pc.localDescription.sdp });
    };

    const handle = async (m) => {
      if (m.type === "offer") {
        if (st.target && m.from !== st.target) return;
        st.target = m.from;
        const pc = createPc(m.from);
        setStatus("connecting");
        await pc.setRemoteDescription(m.data);
        await pc.setLocalDescription(await pc.createAnswer());
        signal(m.from, "answer", { type: pc.localDescription.type, sdp: pc.localDescription.sdp });
        await flushIce();
      } else if (m.type === "answer") {
        if (st.pc?._target === m.from && st.pc.signalingState === "have-local-offer") { await st.pc.setRemoteDescription(m.data); await flushIce(); }
      } else if (m.type === "ice") {
        if (st.pc?._target !== m.from) return;
        if (st.pc.remoteDescription) { try { await st.pc.addIceCandidate(m.data); } catch { /* noop */ } } else st.pending.push(m.data);
      } else if (m.type === "bye" && st.pc?._target === m.from) {
        closePc(); st.target = null; setStatus("waiting");
      }
    };

    const tick = async () => {
      if (!st.alive) return;
      try {
        const q = new URLSearchParams({ peer_id: st.peerId, name: displayName || "", since: st.cursor, role });
        const r = await fetch(`${base}/poll?${q}`, { cache: "no-store" });
        if (r.ok) {
          const d = await r.json();
          st.cursor = d.cursor || st.cursor;
          for (const m of d.messages || []) { try { await handle(m); } catch { /* mensagem inválida */ } }
          const peers = d.peers || [];
          const preferred = (role && peers.find((p) => p.role && p.role !== role)) || peers[0];
          if (st.target && preferred?.peer_id !== st.target) { closePc(); st.target = null; }
          if (!st.target && preferred) st.target = preferred.peer_id;
          if (!st.target) setStatus("waiting");
          else {
            const pc = st.pc;
            const stuck = pc && pc.connectionState !== "connected" && Date.now() - st.startedAt > CONNECT_TIMEOUT_MS;
            if (stuck) { st.attempts += 1; closePc(); }
            if (!st.pc && st.peerId < st.target) await offer(st.target);
            else if (!st.pc) setStatus("connecting");
          }
        }
      } catch { /* rede instável: tenta de novo no próximo ciclo */ }
      if (st.alive) st.timer = setTimeout(tick, POLL_MS);
    };

    const leave = () => {
      if (st.target) signal(st.target, "bye", null);
      fetch(`${base}/leave`, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ peer_id: st.peerId }) }).catch(() => {});
    };

    const init = async () => {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch {
        try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { if (st.alive) setStatus("denied"); return; }
      }
      if (!st.alive) { stream.getTracks().forEach((t) => t.stop()); return; }
      st.localStream = stream; setLocalStream(stream);
      try {
        const rc = await fetch(`${API}/api/rtc/ice`);
        if (rc.ok) { const cfg = await rc.json(); if (cfg?.iceServers?.length) st.ice = cfg; }
      } catch { /* mantém STUN */ }
      setStatus("waiting");
      tick();
    };

    init();
    window.addEventListener("pagehide", leave);
    return () => {
      st.alive = false;
      clearTimeout(st.timer);
      window.removeEventListener("pagehide", leave);
      leave();
      closePc();
      st.target = null; st.cursor = "";
      st.localStream?.getTracks().forEach((t) => t.stop());
      st.localStream = null;
      st.peerId = newId();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  return { status, localStream, remoteStream, hangup: () => { s.current.alive = false; } };
};
